/**
 * Homing sync protocol v1: the contract between a MailboxDO and every client.
 *
 *   client                                   server (MailboxDO behind the worker)
 *   ──────                                   ──────
 *   WS  /api/accounts/:id/sync  ◀── poke ──  {type:"poke", version}   (on every change)
 *   POST /api/accounts/:id/pull  {since} ──▶ {changes | snapshot page, version, more}
 *   POST /api/accounts/:id/push  {mutations} ─▶ {results}
 *
 * - `version` is the MailboxDO's monotonic change counter (change_log). A client stores
 *   the highest version it has applied and pulls everything after it.
 * - If `since` is older than the compaction floor, the server answers with a paginated
 *   snapshot instead (`snapshot: true`), cursor-driven; the client replaces its tables.
 * - Pushes are idempotent by mutation id: re-sending an applied mutation returns its
 *   original result. Order within a push is preserved.
 * - WebSocket carries only pokes (hibernation-friendly); data always moves over pull.
 * - All bodies are JSON, all non-GET requests carry `X-Homing: 1` (CSRF guard).
 */
import { z } from "zod";

export const PROTOCOL_VERSION = 1;

/** Tables that sync to clients. Server-only tables (timers, audit_log, …) never do. */
export const SyncedTable = z.enum(["labels", "threads", "messages", "attachments", "drafts", "contacts", "calendars", "events"]);
export type SyncedTable = z.infer<typeof SyncedTable>;

export const Change = z.discriminatedUnion("op", [
  z.object({ v: z.number().int().positive(), table: SyncedTable, key: z.string(), op: z.literal("put"), row: z.record(z.string(), z.unknown()) }),
  z.object({ v: z.number().int().positive(), table: SyncedTable, key: z.string(), op: z.literal("del") }),
]);
export type Change = z.infer<typeof Change>;

export const PullRequest = z.object({
  protocol: z.literal(PROTOCOL_VERSION),
  since: z.number().int().nonnegative(),
  /** opaque cursor when continuing a snapshot */
  cursor: z.string().optional(),
  limit: z.number().int().positive().max(5000).default(1000),
});
export type PullRequest = z.infer<typeof PullRequest>;

export const PullResponse = z.object({
  /** server version this page brings the client to (apply all changes, then store it) */
  version: z.number().int().nonnegative(),
  snapshot: z.boolean(),
  /** in a snapshot, the first page tells the client to clear synced tables first */
  reset: z.boolean().default(false),
  changes: z.array(Change),
  more: z.boolean(),
  cursor: z.string().optional(),
  /** server-side sync health, for the UI ("backfilling 12,400 / ~98,000") */
  status: z
    .object({
      phase: z.enum(["connecting", "initial", "backfill", "live", "reauth_required", "error"]),
      backfilled: z.number().int().nonnegative().optional(),
      estimatedTotal: z.number().int().nonnegative().optional(),
      lastSyncAt: z.number().int().optional(),
    })
    .optional(),
});
export type PullResponse = z.infer<typeof PullResponse>;

// ---------- mutations ----------

const ThreadIds = z.array(z.string().min(1)).min(1).max(1000);

/**
 * Every triage mutation is a label delta on threads (Gmail's own model), so one pure
 * function (applyLabelDelta in mutations.ts) runs identically on client and server.
 */
export const MutationArgs = {
  "thread.modifyLabels": z.object({ threadIds: ThreadIds, add: z.array(z.string()).default([]), remove: z.array(z.string()).default([]) }),
} as const;
export type MutationName = keyof typeof MutationArgs;

export const Mutation = z.object({
  id: z.string().min(8).max(64), // client-generated, globally unique (uuid v7)
  name: z.enum(Object.keys(MutationArgs) as [MutationName, ...MutationName[]]),
  args: z.unknown(),
  clientTime: z.number().int(),
});
export type Mutation = z.infer<typeof Mutation>;

export const PushRequest = z.object({ protocol: z.literal(PROTOCOL_VERSION), mutations: z.array(Mutation).min(1).max(100) });
export type PushRequest = z.infer<typeof PushRequest>;

export const MutationResult = z.discriminatedUnion("ok", [
  z.object({ id: z.string(), ok: z.literal(true), version: z.number().int() }),
  /** permanent failure: client drops the mutation and its optimistic effect */
  z.object({ id: z.string(), ok: z.literal(false), error: z.string() }),
]);
export type MutationResult = z.infer<typeof MutationResult>;

export const PushResponse = z.object({ results: z.array(MutationResult) });
export type PushResponse = z.infer<typeof PushResponse>;

export const Poke = z.object({ type: z.literal("poke"), version: z.number().int() });
export type Poke = z.infer<typeof Poke>;

// ---------- accounts ----------

export const AccountSummary = z.object({
  id: z.string(),
  provider: z.enum(["google", "microsoft"]),
  email: z.string(),
  displayName: z.string(),
  status: z.enum(["active", "reauth_required", "disconnected"]),
});
export type AccountSummary = z.infer<typeof AccountSummary>;
